import { CloudAnomalyAlert } from "../../types/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../ui/common-ui/dialog";
import { Badge } from "../../../../ui/common-ui/badge";
import { Button } from "../../../../ui/common-ui/button";
import { Separator } from "../../../../ui/common-ui/separator";
import { Card, CardContent } from "../../../../ui/common-ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../../ui/common-ui/collapsible";
import { JsonViewer } from "../../../../ui/common-ui/json-viewer";
import {
  AlertTriangle,
  User,
  Cloud,
  Copy,
  CheckCircle2,
  Brain,
  BookOpen,
  Route,
  FileText,
  Network,
  Activity,
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { format, isValid } from "date-fns";
import { useState } from "react";
import { useToast } from "../../hooks/use-toast";

interface AlertDetailsProps {
  alert: CloudAnomalyAlert | null;
  open: boolean;
  onClose: () => void;
}

const getSeverityConfig = (severity: string) => {
  const normalized = severity.toLowerCase();
  const configs: Record<string, { className: string; icon: typeof AlertTriangle; label: string }> = {
    critical: { className: "severity-critical", icon: AlertTriangle, label: "Critical" },
    high: { className: "severity-high", icon: AlertTriangle, label: "High" },
    medium: { className: "severity-medium", icon: AlertTriangle, label: "Medium" },
  };
  return configs[normalized] || configs.medium;
};

const getAlertTypeConfig = (alertType: string) => {
  const configs = {
    BEHAVIOUR: { icon: Brain, label: "Behaviour", className: "bg-purple-100 text-purple-700 border-purple-300" },
    RULE: { icon: BookOpen, label: "Rule", className: "bg-blue-100 text-blue-700 border-blue-300" },
    SEQUENTIAL: { icon: Route, label: "Sequential", className: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  };
  return configs[alertType as keyof typeof configs] || configs.RULE;
};

const getActionIcon = (action?: string) => {
  const a = (action ?? "").toLowerCase();
  if (a.includes("iam") || a.includes("serviceaccount")) return User;
  if (a.includes("storage") || a.includes("bucket")) return FileText;
  if (a.includes("network") || a.includes("firewall")) return Network;
  return Activity;
};

const getLogTime = (logData: any): string | undefined => {
  return (
    logData?.timestamp ||
    logData?.eventTime ||
    logData?.time ||
    logData?.protoPayload?.request_metadata?.request_attributes?.time ||
    logData?.protoPayload?.requestMetadata?.requestAttributes?.time
  );
};

const safeFormatTimestamp = (time?: string) => {
  if (!time) return undefined;
  const d = new Date(time);
  if (!isValid(d)) return undefined;
  return format(d, "yyyy-MM-dd HH:mm:ss");
};

const inferStepFromLog = (logData: any) => {
  const action =
    logData?.protoPayload?.method_name ||
    logData?.protoPayload?.methodName ||
    logData?.eventName ||
    logData?.operationName;

  const resource =
    logData?.protoPayload?.resource_name ||
    logData?.protoPayload?.resourceName ||
    logData?.protoPayload?.authorization_info?.[0]?.resource ||
    logData?.protoPayload?.authorizationInfo?.[0]?.resource ||
    logData?.resourceName ||
    logData?.resource?.type;

  const ip =
    logData?.protoPayload?.request_metadata?.caller_ip ||
    logData?.protoPayload?.requestMetadata?.callerIp ||
    logData?.sourceIPAddress;

  const region =
    logData?.resource?.labels?.region ||
    logData?.resource?.labels?.zone ||
    logData?.awsRegion ||
    logData?.region ||
    logData?.protoPayload?.resource_location?.current_locations?.[0] ||
    logData?.protoPayload?.resourceLocation?.currentLocations?.[0];

  const time = getLogTime(logData);

  return {
    action: action ?? "Unknown action",
    resource: resource ?? "—",
    ip,
    region,
    time,
  };
};

const buildTimelineSteps = (alert: CloudAnomalyAlert) => {
  const attackNums = Object.keys(alert.attackPath ?? {}).map(Number);
  const logNums = Object.keys(alert.logs ?? {}).map(Number);

  const steps = Array.from(new Set([...attackNums, ...logNums]))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  return steps.map((step) => {
    const ap = (alert.attackPath as any)?.[step];
    const logData = (alert.logs as any)?.[step];
    const inferred = logData ? inferStepFromLog(logData) : undefined;

    return {
      step,
      action: ap?.action ?? inferred?.action ?? `Step ${step}`,
      resource: ap?.resource ?? inferred?.resource ?? "—",
      ip: ap?.ip ?? inferred?.ip,
      region: ap?.region ?? inferred?.region,
      time: ap?.time ?? inferred?.time,
      logData,
    };
  });
};

export const AlertDetails = ({ alert, open, onClose }: AlertDetailsProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [fullyExpandedLogs, setFullyExpandedLogs] = useState<Set<number>>(new Set());
  const { toast } = useToast();

  if (!alert) return null;

  const severityConfig = getSeverityConfig(alert.severity);
  const alertTypeConfig = getAlertTypeConfig(alert.alertType);
  const SeverityIcon = severityConfig.icon;
  const AlertTypeIcon = alertTypeConfig.icon;

  const timelineSteps = buildTimelineSteps(alert);
  const mitreEntries = Object.entries(alert.mitre);
  // Both RULE and SEQUENTIAL alerts use the same timeline view (RULE has 1 step, SEQUENTIAL has multiple)
  const hasTimelineView = alert.alertType === "SEQUENTIAL" || alert.alertType === "RULE";
  const hasTimelineSteps = timelineSteps.length > 0;

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast({
        title: "Copied to clipboard",
        description: `${fieldName} has been copied to your clipboard.`,
      });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Unable to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  const toggleLogExpanded = (step: number) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
      }
      return next;
    });
  };

  const toggleFullyExpandedLog = (step: number) => {
    setFullyExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(step)) {
        next.delete(step);
      } else {
        next.add(step);
      }
      return next;
    });
  };

  const CopyButton = ({ text, fieldName }: { text: string; fieldName: string }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => copyToClipboard(text, fieldName)}
      className="h-6 w-6 p-0 hover:bg-primary/10"
    >
      {copiedField === fieldName ? (
        <CheckCircle2 className="h-3 w-3 text-green-400" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col [&_*]:scrollbar-none [&_*]:[-ms-overflow-style:none] [&_*]:[scrollbar-width:none]">
        {/* Clean Header */}
        <DialogHeader className="space-y-3 pb-4 border-b">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <Badge variant={hasTimelineView ? "default" : "outline"} className={alertTypeConfig.className}>
                  <AlertTypeIcon className="h-3 w-3 mr-1.5" />
                  {alertTypeConfig.label}
                </Badge>
                <Badge
                  variant="outline"
                  className={`${severityConfig.className} w-fit`}
                >
                  <SeverityIcon className="h-3 w-3 mr-1.5" />
                  {severityConfig.label}
                </Badge>
              </div>
              <DialogTitle className="text-2xl font-bold leading-tight">
                {alert.title}
              </DialogTitle>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {hasTimelineView && hasTimelineSteps && (
                  <>
                    <span>•</span>
                    <span>
                      {timelineSteps.length} attack step{timelineSteps.length !== 1 ? "s" : ""}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 space-y-6 py-4">
          {/* Quick Info */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Entity</p>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium truncate">{alert.entity}</p>
                <CopyButton text={alert.entity} fieldName="Entity" />
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Cloud Provider</p>
              <div className="flex items-center gap-2">
                <Cloud className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">{alert.cloudType}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Alert Type</p>
              <div className="flex items-center gap-2">
                <AlertTypeIcon className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">{alertTypeConfig.label}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Attack Timeline - Only for RULE and SEQUENTIAL */}
          {hasTimelineView && (
            <>
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Attack Timeline
                </h3>

                {hasTimelineSteps ? (
                  <div className="relative space-y-3 pl-8">
                    {/* Timeline line */}
                    {timelineSteps.length > 1 && (
                      <div className="absolute left-2 top-4 bottom-4 w-px bg-border" />
                    )}

                    {timelineSteps.map((stepData, index) => {
                      const step = stepData.step;
                      const ActionIcon = getActionIcon(stepData.action);
                      const isFirst = index === 0;
                      const isLast = index === timelineSteps.length - 1;
                      const logData = stepData.logData;
                      const isLogExpanded = expandedLogs.has(step);
                      const stepTime = safeFormatTimestamp(stepData.time);

                      return (
                        <div key={step} className="relative">
                          {/* Timeline dot - always show for RULE and SEQUENTIAL */}
                          <div className={`absolute -left-8 top-3 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 ${isFirst ? 'bg-primary border-primary' :
                            isLast ? 'bg-destructive border-destructive' :
                              'bg-background border-border'
                            }`}>
                            <span className={`text-[10px] font-bold ${isFirst || isLast ? 'text-white' : 'text-foreground'
                              }`}>
                              {step}
                            </span>
                          </div>

                          <Card className={`${timelineSteps.length > 1 && isFirst ? "border-primary/30" :
                            timelineSteps.length > 1 && isLast ? "border-destructive/30" :
                              ""
                            }`}>
                            <CardContent className="p-4 space-y-3">
                              {/* Action & Time */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <ActionIcon className="h-4 w-4 text-primary flex-shrink-0" />
                                  <p className="font-mono text-xs font-medium break-all">
                                    {stepData.action}
                                  </p>
                                </div>
                                {stepTime && (
                                  <span className="text-xs text-muted-foreground font-mono">{stepTime}</span>
                                )}
                              </div>

                              {/* Resource */}
                              <div className="bg-muted/50 rounded p-2 text-xs">
                                <p className="text-muted-foreground mb-1">Resource</p>
                                <p className="font-mono text-foreground break-all">{stepData.resource}</p>
                              </div>

                              {/* Details */}
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                {stepData.ip && (
                                  <div>
                                    <p className="text-muted-foreground mb-0.5">IP Address</p>
                                    <p className="font-mono">{stepData.ip}</p>
                                  </div>
                                )}
                                {stepData.region && (
                                  <div>
                                    <p className="text-muted-foreground mb-0.5">Region</p>
                                    <p>{stepData.region}</p>
                                  </div>
                                )}
                              </div>

                              {/* Raw Log Viewer */}
                              {logData && (
                                <Collapsible open={isLogExpanded} onOpenChange={() => toggleLogExpanded(step)}>
                                  <div className="flex items-center justify-between pt-2 border-t">
                                    <CollapsibleTrigger className="flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors">
                                      {isLogExpanded ? (
                                        <ChevronDown className="h-3 w-3" />
                                      ) : (
                                        <ChevronRight className="h-3 w-3" />
                                      )}
                                      View Raw Log
                                    </CollapsibleTrigger>
                                    {isLogExpanded && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleFullyExpandedLog(step);
                                        }}
                                        className="h-6 px-2 text-xs text-muted-foreground hover:text-primary"
                                      >
                                        {fullyExpandedLogs.has(step) ? (
                                          <>
                                            <Minimize2 className="h-3 w-3 mr-1" />
                                            Collapse
                                          </>
                                        ) : (
                                          <>
                                            <Maximize2 className="h-3 w-3 mr-1" />
                                            Expand
                                          </>
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                  <CollapsibleContent className="pt-2">
                                    <JsonViewer
                                      data={logData}
                                      defaultExpanded={false}
                                      forceExpandAll={fullyExpandedLogs.has(step) ? true : undefined}
                                    />
                                  </CollapsibleContent>
                                </Collapsible>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground p-4 border rounded-md bg-muted/30">
                    No attack path data available for this alert.
                  </div>
                )}
              </div>

              <Separator />
            </>
          )}

          {/* Analysis Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Analysis
            </h3>

            <div className="space-y-3">
              {alert.info.why && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    Why this matters
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {alert.info.why}
                  </p>
                </div>
              )}

              {alert.info.what && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    What happened
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {alert.info.what}
                  </p>
                </div>
              )}

              {alert.info.how && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    Recommended actions
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {alert.info.how}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* MITRE ATT&CK */}
          {mitreEntries.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  MITRE ATT&CK Techniques
                </h3>
                <div className="flex flex-wrap gap-2">
                  {mitreEntries.map(([code, name]) => (
                    <Badge
                      key={code}
                      variant="outline"
                      className="font-mono text-xs"
                      title={name}
                    >
                      {code}: {name}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Timeline */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Detection Timeline
            </h3>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-muted-foreground mb-1">Window Start</p>
                <p className="font-mono">
                  {format(new Date(alert.timeRangeStart), "MMM dd, HH:mm:ss")}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Window End</p>
                <p className="font-mono">
                  {format(new Date(alert.timeRangeEnd), "MMM dd, HH:mm:ss")}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Alert Created</p>
                <p className="font-mono">
                  {format(new Date(alert.createdAt), "MMM dd, HH:mm:ss")}
                </p>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Technical Details
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Tenant</p>
                <p className="font-mono">{alert.tenantName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Cloud Account ID</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono truncate">{alert.cloudAccountId}</p>
                  <CopyButton text={alert.cloudAccountId} fieldName="Account ID" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
